import React, { useCallback, useState, useRef, useEffect } from 'react'
import {
  Box,
  Flex,
  Text,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Portal,
  Avatar,
  Tag,
  TagLabel,
  Checkbox,
  Select,
  Button,
  Spinner,
  HStack,
} from '@chakra-ui/react'
import { SearchLgIcon, ChevronRightIcon, ChevronLeftIcon, XCloseIcon } from '../../../assets/icons'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'

interface Option {
  key: string
  name: string
  code?: string
  profile_picture?: string
  first_name?: string
  last_name?: string
}

interface ModalMultiSelectProps {
  label?: string
  hideLabel?: boolean
  options: Option[]
  selectedValues: Option[]
  onChange: (selectedList: Option[]) => void
  onSearchChange?: (v: string) => void
  selectionLimit?: number
  isInvalid?: boolean
  placeholder?: string
  showCountryFlag?: boolean
  hasProfilePicture?: boolean
  isDisabled?: boolean
  isTop?: boolean
  /** Infinite scroll */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  isPaginated?: boolean
  totalPages?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  isLoading?: boolean
  searchDisabled?: boolean
  hasNextPage?: boolean
  compact?: boolean
}

const ModalMultiSelect: React.FC<ModalMultiSelectProps> = ({
  label,
  hideLabel = false,
  options,
  selectedValues,
  onChange,
  onSearchChange,
  selectionLimit,
  isInvalid,
  placeholder,
  showCountryFlag = false,
  hasProfilePicture = false,
  isDisabled = false,
  isTop = false,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isPaginated = false,
  totalPages = 1,
  currentPage = 1,
  onPageChange = () => {},
  isLoading = false,
  hasNextPage = false,
  searchDisabled = false,
  compact = false,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const placeholderText = placeholder ?? t('common.placeholders.selectOptions')
  const triggerMinHeight = compact ? '34px' : '40px'
  const triggerPadding = compact ? '4px 10px' : '6px 12px'
  const tagHeight = compact ? '22px' : '28px'
  const tagRadius = compact ? '11px' : '14px'
  const flagSize = compact ? 16 : 20

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      if (onSearchChange) onSearchChange('')
    }
  }, [isOpen, onSearchChange])

  useEffect(() => {
    if (!isOpen) return

    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return
      }
      setIsOpen(false)
    }
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  // Prevent scroll chaining / background scroll while dropdown is open.
  useEffect(() => {
    if (!isOpen) return

    const body = document.body
    const html = document.documentElement

    const countAttr = 'data-filtermodal-scroll-lock-count'
    const prevOverflowAttr = 'data-filtermodal-prev-overflow'
    const prevPaddingAttr = 'data-filtermodal-prev-padding-right'

    const count = Number(body.getAttribute(countAttr) ?? '0')
    if (count === 0) {
      body.setAttribute(prevOverflowAttr, body.style.overflow || '')
      body.setAttribute(prevPaddingAttr, body.style.paddingRight || '')

      const scrollbarWidth = window.innerWidth - html.clientWidth
      body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
    }
    body.setAttribute(countAttr, String(count + 1))

    return () => {
      const nextCount = Number(body.getAttribute(countAttr) ?? '1') - 1
      if (nextCount <= 0) {
        const prevOverflow = body.getAttribute(prevOverflowAttr) ?? ''
        const prevPaddingRight = body.getAttribute(prevPaddingAttr) ?? ''

        body.style.overflow = prevOverflow
        body.style.paddingRight = prevPaddingRight

        body.removeAttribute(countAttr)
        body.removeAttribute(prevOverflowAttr)
        body.removeAttribute(prevPaddingAttr)
      } else {
        body.setAttribute(countAttr, String(nextCount))
      }
    }
  }, [isOpen])

  const isRemoteSearch = Boolean(onSearchChange)

  const filteredOptions = isPaginated
    ? options
    : isRemoteSearch
      ? options
      : search
        ? options.filter((option) => option.name.toLowerCase().includes(search.toLowerCase()))
        : options

  const handleSelectOption = (option: any) => {
    if (selectedValues.some((v) => v.key === option.key)) {
      onChange(selectedValues.filter((v) => v.key !== option.key))
    } else if (!selectionLimit || selectedValues.length < selectionLimit) {
      onChange([...selectedValues, option])
    }
    setSearch('')
    if (onSearchChange) onSearchChange('')
  }

  const handleListScroll = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const el = dropdownRef.current
    if (!el) return

    const list = el.querySelector('[data-modal-multiselect-scroll]') as HTMLDivElement | null
    if (!list) return

    const { scrollTop, scrollHeight, clientHeight } = list
    const threshold = 60
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onLoadMore()
    }
  }, [onLoadMore, hasMore, isLoadingMore])

  return (
    <Box width="100%" zIndex={1} minW="150px">
      {label && !hideLabel && (
        <Text textStyle="l-xs" color="solid.500" mb={1}>
          {label}
        </Text>
      )}
      <Box borderRadius="6px" position="relative" ref={ref}>
        <Box w="100%" ref={triggerRef} position="relative">
          <Flex
            pos="absolute"
            h="100%"
            w="calc(100% - 32px)"
            alignItems="center"
            p={triggerPadding}
            gap="6px"
            pointerEvents={isDisabled ? 'none' : 'auto'}
            flexWrap="wrap"
            overflowY="auto"
          >
            {(() => {
              const maxTagsToShow = 2
              const tagsToShow = selectedValues.slice(0, maxTagsToShow)
              const hiddenCount = selectedValues.length - tagsToShow.length

              return (
                <>
                  {tagsToShow.map((option) => (
                    <Tag
                      key={option.key}
                      h={tagHeight}
                      borderRadius={tagRadius}
                      gap={compact ? '6px' : '8px'}
                      bg="solid.50"
                      maxW="full"
                      overflow="hidden"
                      whiteSpace="nowrap"
                    >
                      {showCountryFlag && option.code && (
                        <CircleFlag
                          countryCode={option.code}
                          style={{
                            width: `${flagSize}px`,
                            height: `${flagSize}px`,
                            borderRadius: '4px',
                            marginRight: '4px',
                          }}
                        />
                      )}
                      {hasProfilePicture && <Avatar src={option.profile_picture} boxSize={compact ? '16px' : '20px'} mr={1} />}
                      <TagLabel fontSize={compact ? '11px' : undefined}>{option.name}</TagLabel>
                      {!isDisabled && (
                        <Flex
                          as="button"
                          aria-label={`Remove ${option.name}`}
                          alignItems="center"
                          justifyContent="center"
                          boxSize="16px"
                          borderRadius="full"
                          ml={-1}
                          role="group"
                          _hover={{ bg: 'solid.100' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            onChange(selectedValues.filter((v) => v.key !== option.key))
                          }}
                        >
                          <Icon width="10px" height="10px" color="solid.400" _groupHover={{ color: 'error.100' }}>
                            <XCloseIcon />
                          </Icon>
                        </Flex>
                      )}
                    </Tag>
                  ))}
                  {hiddenCount > 0 && (
                    <Tag h={tagHeight} borderRadius={tagRadius} bg="solid.50" maxW="full" overflow="hidden" whiteSpace="nowrap">
                      <TagLabel fontSize={compact ? '11px' : undefined}>+ {hiddenCount}</TagLabel>
                    </Tag>
                  )}
                </>
              )
            })()}
          </Flex>
          <Select
            pos="relative"
            bg="transparent"
            variant={'forms'}
            cursor="pointer"
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setIsOpen(!isOpen)
            }}
            placeholder={selectedValues.length > 0 ? '' : placeholderText}
            isDisabled={isDisabled}
            color="solid.300"
            _placeholder={{ color: 'solid.300' }}
            sx={{
              color: 'solid.500',
            }}
            value=""
            borderColor={isInvalid ? 'error.100' : 'solid.100'}
            borderRadius="6px"
            minH={triggerMinHeight}
            style={{ color: 'solid.500' }}
          />
        </Box>

        {isOpen && !isDisabled && (
          <Portal>
            <Box
              ref={dropdownRef}
              position="fixed"
              left={triggerRef.current?.getBoundingClientRect().left}
              top={
                !isTop
                  ? triggerRef.current
                    ? triggerRef.current.getBoundingClientRect().top + (triggerRef.current.offsetHeight || 0) + 5
                    : undefined
                  : undefined
              }
              bottom={
                isTop ? window.innerHeight - (triggerRef.current?.getBoundingClientRect().top || 0) + 5 : undefined
              }
              width={triggerRef.current?.offsetWidth}
              minH="120px"
              bg="white"
              zIndex={20000}
              borderRadius="8px"
              borderColor="solid.100"
              boxShadow="0px 2px 8px -2px #00000014, 0px 2px 14px -3px #0000001F"
              borderWidth="1px"
              minW="200px"
            >
              {!searchDisabled && (
                <>
                  <Box px={3} pt={2} pb={2}>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none" h="full">
                        <Icon width="16px" height="16px" fill="none" color="solid.300">
                          <SearchLgIcon />
                        </Icon>
                      </InputLeftElement>
                      <Input
                        variant="filterSearch"
                        placeholder={t('common.labels.searchPlaceholder')}
                        fontSize="13px"
                        borderRadius="6px"
                        bg="solid.25"
                        h="34px"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value)
                          if (onSearchChange) onSearchChange(e.target.value)
                        }}
                        autoFocus
                      />
                    </InputGroup>
                  </Box>
                  <Divider borderColor="solid.50" my={0} />
                </>
              )}
              <Box
                data-modal-multiselect-scroll
                maxH="200px"
                overflowY="auto"
                px={2}
                pt={1}
                pb={1}
                sx={{ overscrollBehavior: 'contain' }}
                onScroll={onLoadMore ? handleListScroll : undefined}
              >
                {isLoading ? (
                  <Flex justify="center" align="center" height="100px">
                    <Spinner color="primary.100" size="sm" />
                  </Flex>
                ) : filteredOptions.length === 0 ? (
                  <Text textStyle="l-xs" color="solid.300" textAlign="center" px={2} py={2}>
                    {t('common.labels.noOptionsFound')}
                  </Text>
                ) : (
                  <>
                    {filteredOptions.map((option) => {
                      const isSelected = selectedValues.some((v) => v.key === option.key)
                      const isLimit = selectionLimit && selectedValues.length >= selectionLimit && !isSelected
                      return (
                        <Flex
                          key={option.key}
                          alignItems="center"
                          gap="12px"
                          px={2}
                          py="7px"
                          borderRadius="6px"
                          cursor={isLimit ? 'not-allowed' : 'pointer'}
                          transition="background 0.15s ease"
                          _hover={{ bg: isLimit ? undefined : 'solid.25' }}
                          opacity={isLimit ? 0.5 : 1}
                          onClick={() => !isLimit && handleSelectOption(option)}
                        >
                          <Checkbox isChecked={isSelected} isDisabled={isLimit || isDisabled} pointerEvents="none" mr={2} />
                          {showCountryFlag && option.code && (
                            <CircleFlag
                              countryCode={option.code}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px',
                              }}
                            />
                          )}
                          {hasProfilePicture && <Avatar src={option.profile_picture} boxSize="20px" />}
                          <Text textStyle="l-xs" color="solid.700" noOfLines={1}>
                            {option.name}
                          </Text>
                        </Flex>
                      )
                    })}
                    {onLoadMore && hasMore && isLoadingMore && (
                      <Flex justify="center" py={2}>
                        <Spinner size="sm" color="solid.400" />
                      </Flex>
                    )}
                  </>
                )}
              </Box>
              {isPaginated && !onLoadMore && totalPages > 1 && (
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                  borderTop="1px"
                  borderColor="solid.50"
                  px="10px"
                  py="5px"
                  paddingLeft="20px"
                >
                  <Text fontSize="xs" color="solid.400">
                    {currentPage} {t('table.of')} {totalPages}
                  </Text>
                  <HStack spacing={0}>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                      isDisabled={currentPage <= 1 || isLoading}
                      leftIcon={
                        <Icon width="14px" height="14px" viewBox="0 0 24 24" color="solid.500">
                          <ChevronLeftIcon />
                        </Icon>
                      }
                    >
                      {t('common.actions.previous')}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => onPageChange(currentPage + 1)}
                      isDisabled={!hasNextPage || isLoading || currentPage >= totalPages}
                      rightIcon={
                        <Icon width="14px" height="14px" viewBox="0 0 24 24" color="solid.500">
                          <ChevronRightIcon />
                        </Icon>
                      }
                    >
                      {t('common.actions.next')}
                    </Button>
                  </HStack>
                </Flex>
              )}
            </Box>
          </Portal>
        )}
      </Box>
    </Box>
  )
}

export default ModalMultiSelect
